<template>
  <!-- Figma "Service & Repair - Details - View Evaluation (Read-only)" (5524-4473) and "Approve Quote" (5535-1394):
       one page, two tabs; Job Details → "View Evaluation" opens Recommended, "Approved Quote" opens Approve Quote (?tab=approve).
       API: GET bookings/diagnosis/summary/quote (G-18). Missing in the API (backend gaps G-68 … G-71): quote number,
       submit date, full price breakdown, quote PDF, approve on the consumer's behalf with a signature. -->
  <main class="evalFigma w-full viewEvalPg">
    <EvalShell :title="(quote?.is_approved ? 'Approved Quote' : 'Quote') + (quote?.quote_number ? ' ' + quote.quote_number : '')" sub-title="" crumb="View Evaluation"
      :job-id="$route.params.id" :show-details="false">
      <template #alert>
        <div v-if="quote"
          class="flex flex-wrap items-center justify-between gap-[10px] mb-[15px] px-[16px] py-[10px] rounded-[10px] border-1 border-[#C9DAF8] bg-[#EEF4FE] font-roboto">
          <p class="flex items-center gap-[10px] m-0 text-[14px] leading-[20px] text-[#2A5BB5]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" class="flex-shrink-0">
              <circle cx="12" cy="12" r="9.5" stroke="#2A5BB5" stroke-width="1.6" />
              <path d="M12 11v5M12 8h.01" stroke="#2A5BB5" stroke-width="1.8" stroke-linecap="round" />
            </svg>
            {{ bannerText }}
          </p>
          <button v-if="!quote.is_approved" type="button"
            class="ml-auto h-[32px] px-[14px] rounded-[6px] border-1 border-[#7695FF] bg-[#fff] text-[13px] text-[#2A5BB5] hover:bg-[#EEF2FF]"
            @click="$router.push(`/select/unit/${$route.params.id}`)">
            Edit Evaluation
          </button>
        </div>
        <div v-if="quote" class="flex gap-[8px] mb-[15px] font-roboto">
          <button v-for="t in visibleTabs" :key="t.key" type="button"
            class="mobile:flex-1 laptop:flex-none h-[34px] px-[18px] rounded-full border-1 text-[13px] font-medium transition-colors"
            :class="tab === t.key ? 'bg-[#7695FF] border-[#7695FF] text-white' : 'bg-[#fff] border-[#E1E5EF] text-[#333333] hover:bg-[#F4F5FB]'"
            @click="setTab(t.key)">
            {{ t.label }}
          </button>
        </div>
      </template>

      <template #title-extra>
        <span v-if="quote" class="px-[10px] py-[3px] rounded-[6px] text-[12px] font-medium font-roboto"
          :class="quote.is_approved ? 'bg-[#E7F7F0] text-[#1FA971]' : 'bg-[#FEF6E6] text-[#C98305]'">
          {{ quote.is_approved ? 'Approved' : 'Awaiting Approval' }}
        </span>
      </template>

      <p v-if="loading" class="m-0 py-[40px] text-center text-[14px] text-[#999999] font-roboto">Loading quote…</p>
      <p v-else-if="!hasQuote" class="m-0 py-[40px] text-center text-[14px] text-[#999999] font-roboto">
        No quote is available for this job yet.
      </p>
      <template v-else>
        <div v-if="tab === 'approve' && !quote.is_approved"
          class="flex items-start gap-[10px] mb-[15px] px-[14px] py-[10px] rounded-[8px] bg-[#EEF4FE] font-roboto">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" class="flex-shrink-0 mt-[2px]">
            <circle cx="12" cy="12" r="9.5" stroke="#2A5BB5" stroke-width="1.6" />
            <path d="M12 11v5M12 8h.01" stroke="#2A5BB5" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          <p class="m-0 text-[13px] leading-[18px] text-[#2A5BB5]"><b class="font-medium">Review the items below before
              approving.</b><br>Approving authorizes the work listed and confirms the total amount shown.</p>
        </div>

        <summary_detail_card :details="shown" :included="tab === 'approve'" />

        <div class="flex items-center justify-between gap-[16px] mt-[14px] px-[20px] py-[14px] rounded-[10px] bg-[#F3F4F7] font-roboto">
          <span class="flex flex-col">
            <span class="text-[17px] leading-[25px] font-semibold text-[#2E3138]">Total Amount</span>
            <span v-if="tab === 'approve'" class="text-[12px] text-[#5B616B]">{{ itemCount }} {{ itemCount === 1 ? 'item' : 'items' }}
              included · taxes and fees as shown in Price Summary</span>
          </span>
          <span class="text-[20px] leading-[30px] font-bold text-[#0E2F7E]">{{ shown.total_text }}</span>
        </div>

        <!-- Approve Quote: consumer signs on the technician's device. No pro endpoint yet (G-71) → button stays disabled -->
        <template v-if="tab === 'approve' && !quote.is_approved">
          <p class="mb-0 mt-[14px] text-[12px] leading-[17px] text-[#5B616B] font-roboto">
            By approving, you authorize the company to perform the work listed above at the total shown.
          </p>
          <div class="mt-[12px] p-[14px] rounded-[10px] border-1 border-[#E1E5EF] font-roboto">
            <label class="flex items-start gap-[10px] m-0 cursor-pointer text-[13px] leading-[18px] text-[#333333]">
              <input v-model="agreed" type="checkbox" class="w-[16px] h-[16px] mt-[1px] flex-shrink-0 accent-[#6C79E0] cursor-pointer">
              By signing, I confirm that I have approved this quote and authorize the technician to complete checkout on
              my behalf.
            </label>
            <div class="flex items-center justify-between mt-[14px] mb-[8px]">
              <span class="text-[13px] font-medium text-[#333333]">Consumer Signature</span>
              <button type="button" class="h-[28px] px-[14px] rounded-full border-1 border-[#F67280] bg-[#fff] text-[12px] text-[#F67280] hover:bg-[#FEF1F3]"
                @click="clearSignature">Clear</button>
            </div>
            <div class="h-[140px] rounded-[8px] border-1 border-[#E1E5EF]">
              <VueSignaturePad ref="signaturePad" width="100%" height="100%" :options="{ onEnd: onSignEnd }" />
            </div>
          </div>
        </template>

        <div class="flex flex-wrap justify-center items-start gap-[12px] mt-[24px] font-roboto">
          <!-- G-70 PDF of the set this tab shows (approved quote: approved; otherwise the recommended one) -->
          <button type="button" :disabled="downloading"
            class="flex items-center gap-[8px] h-[36px] px-[16px] rounded-full border-1 border-[#8479E1] bg-[#fff] text-[13px] font-medium text-[#8479E1] hover:bg-[#F4F2FD] disabled:opacity-60"
            @click="downloadPdf">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14" stroke="#8479E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ downloading ? 'Downloading…' : 'Download PDF' }}
          </button>
          <button v-if="tab !== 'approve' || quote.is_approved" type="button"
            class="flex items-center gap-[8px] h-[36px] px-[16px] rounded-full bg-[#00B7C2] text-[13px] font-medium text-white border-0 hover:bg-[#00A3AD]"
            @click="$refs.sendProposalModal.open(consumerEmail)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="18" cy="5" r="2.5" stroke="#fff" stroke-width="2" /><circle cx="6" cy="12" r="2.5" stroke="#fff" stroke-width="2" />
              <circle cx="18" cy="19" r="2.5" stroke="#fff" stroke-width="2" /><path d="M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4" stroke="#fff" stroke-width="2" />
            </svg>
            Share Quote
          </button>
          <span v-else class="flex flex-col items-center gap-[6px]">
            <button type="button" disabled title="Not available yet: the API has no approve endpoint for technicians"
              class="flex items-center gap-[8px] h-[36px] px-[16px] rounded-full bg-[#00B7C2] text-[13px] font-medium text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed">
              Approve Quote
            </button>
            <span class="text-[12px] text-[#999999]">Approving here isn't available yet.</span>
          </span>
        </div>
      </template>

      <!-- Figma Price Summary from price_summary (G-69); older API: Sub Total / Total Discount / Net Total -->
      <template #aside>
        <div class="flex items-center gap-1 pb-[15px]">
          <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Price Summary</p>
        </div>
        <div v-if="hasQuote" class="flex flex-col font-roboto text-[14px]">
          <div v-for="row in summaryRows" :key="row.label" class="flex justify-between py-[10px] border-b-1 border-[#EEF0F4]"
            :class="row.sub ? 'pl-[12px] text-[13px]' : ''" :style="{ color: row.color || '#333333' }">
            <span>{{ row.label }}:</span><span>{{ row.value }}</span>
          </div>
          <div class="flex justify-between items-center mt-[12px] px-[14px] py-[12px] rounded-[8px] bg-[#F3F4F7]">
            <span class="font-medium text-[#333333]">Net Total</span>
            <span class="text-[18px] font-bold text-[#0E2F7E]">{{ shown.price_summary ? money(shown.price_summary.net_total) : shown.total_text }}</span>
          </div>
        </div>
        <p v-else class="m-0 text-[13px] text-[#999999] font-roboto">-</p>
      </template>
    </EvalShell>

    <SendProposalModal ref="sendProposalModal" :booking-id="$route.params.id" :pdf="pdfFile"
      @sent="toast.success('Evaluation report has been emailed successfully.')" @failed="res => toast.error(res?.message || 'Unable to send the quote.')" />
  </main>
</template>

<script>
import EvalShell from "@/components/nest_components/EvalShell.vue";
import summary_detail_card from "@/components/nest_components/summary_detail_card.vue";
import SendProposalModal from "@/components/booking/SendProposalModal.vue";
import { buildQuotePdf } from "@/helpers/quotePdf";
import { evaluationApi } from "@/services/evaluationApi";
import { useToast } from "vue-toastification";
import moment from "moment";

export default {
  components: { EvalShell, summary_detail_card, SendProposalModal },

  data() {
    return {
      toast: useToast(),
      quote: null,
      loading: true,
      tabs: [{ key: "recommended", label: "Recommended" }, { key: "approve", label: "Approve Quote" }],
      agreed: false,
      signed: false,
      downloading: false,
      // job detail passes it in the history state (never in the URL)
      consumerEmail: window.history.state?.consumerEmail || "",
    };
  },

  computed: {
    // G-71 is deferred to the end (product): the technician approving for the consumer (checkbox + signature) stays
    // hidden — the Approve tab only shows an already approved quote. Allow the unapproved case here to bring it back.
    canShowApprove() {
      return !!this.quote?.is_approved;
    },
    visibleTabs() {
      return this.tabs.filter(t => t.key !== "approve" || this.canShowApprove);
    },
    tab() {
      return this.$route.query.tab === "approve" && this.canShowApprove ? "approve" : "recommended";
    },
    // Recommended tab of an approved quote: what the technician recommended (G-72); otherwise the quote itself
    shown() {
      return this.tab === "recommended" && this.quote?.recommended ? this.quote.recommended : this.quote;
    },
    bannerText() {
      const at = (epoch) => (epoch ? moment.unix(epoch).format("MM/DD/YYYY [at] hh:mm A") : "");
      if (this.quote.is_approved) {
        return this.quote.approved_at
          ? `Read-only. This quote was approved by the consumer on ${at(this.quote.approved_at)}.`
          : "Read-only. This quote has been approved by the consumer.";
      }
      return this.quote.submitted_at
        ? `Read-only. This quote was submitted on ${at(this.quote.submitted_at)} and is awaiting consumer approval.`
        : "Read-only. This quote is awaiting consumer approval.";
    },
    // Figma Price Summary rows (net total is the box below)
    summaryRows() {
      const ps = this.shown?.price_summary;
      if (!ps) {
        return [
          { label: "Sub Total", value: this.money(this.shown.sub_total) },
          { label: "Total Discount", value: this.shown.total_discount, color: "#E56A2C" },
        ];
      }
      return [
        { label: "Sub Total", value: this.money(ps.sub_total) },
        { label: "Service Discount", value: this.money(ps.service_discount), sub: true, color: "#5B616B" },
        { label: "Recommended Items Discount", value: this.money(ps.recommended_items_discount), sub: true, color: "#5B616B" },
        { label: "Total Discount", value: this.money(ps.total_discount), color: "#E56A2C" },
        { label: "Amount After Discount", value: this.money(ps.amount_after_discount), color: "#2A5BB5" },
        ...(Number(ps.claim_amount) ? [{ label: "Claim Amount", value: this.money(ps.claim_amount) }] : []),
        { label: "Taxes", value: this.money(ps.taxes) },
        { label: "Platform Fee", value: this.money(ps.platform_fee) },
      ];
    },
    hasQuote() {
      return Array.isArray(this.quote?.items) && this.quote.items.length > 0;
    },
    itemCount() {
      return (this.quote?.items || []).reduce((n, section) => n + (section.data || []).length, 0);
    },
  },

  async mounted() {
    const res = await evaluationApi.quote(this.$route.params.id);
    this.loading = false;
    if (!res.ok) {
      this.toast.error(res.message || "Unable to load the quote.");
      return;
    }
    this.quote = res.body;
  },

  methods: {
    // the tab lives in the URL (replace: switching tabs is not a Back step)
    setTab(key) {
      this.$router.replace({ query: { ...this.$route.query, tab: key === "approve" ? "approve" : undefined } });
    },
    // Figma: negatives in brackets, $(4.00)
    money(value) {
      const amount = Number(value || 0);
      const text = Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return amount < 0 ? `$(${text})` : `$${text}`;
    },
    // G-70: the app draws the PDF of the set this tab shows; quote/pdf adds the parts, else what is on screen
    async buildPdf() {
      const res = await evaluationApi.quotePdfData(this.$route.params.id);
      const body = res.ok && res.body?.items ? res.body : null;
      const quote = body ? (this.tab === "recommended" && body.recommended ? body.recommended : body) : this.shown;
      return buildQuotePdf({ ...this.quote, ...quote }, { bookingId: this.$route.params.id });
    },
    pdfName() {
      return `quote-${this.quote?.quote_number || this.$route.params.id}.pdf`;
    },
    async downloadPdf() {
      this.downloading = true;
      try {
        (await this.buildPdf()).save(this.pdfName());
      } catch (e) {
        this.toast.error("Unable to create the quote PDF.");
      } finally {
        this.downloading = false;
      }
    },
    // the same PDF as Download, for the email (SendProposalModal → summary/quote/pdf/send)
    async pdfFile() {
      const doc = await this.buildPdf();
      return new File([doc.output("blob")], this.pdfName(), { type: "application/pdf" });
    },
    onSignEnd() {
      this.signed = true;
    },
    clearSignature() {
      this.$refs.signaturePad?.clearSignature();
      this.signed = false;
    },
  },
};
</script>
